import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SelectBox from "../../../../../utils/components/SelectBox";
import Checkbox from "../../../../../utils/components/Checkbox";
import Radio from "../../../../../utils/components/Radio";
import Input from "../../../../../utils/components/Input";
import { Translate } from "react-localize-redux";
import { MdOutlineArrowForwardIos, MdLocationOn } from "react-icons/md";
import API from "../../../../../utils/API";
import { useProject } from "../../../../../utils/hooks/useProject";

const weekDays = [
  { label: "monday", value: "1" },
  { label: "tuesday", value: "2" },
  { label: "wednesday", value: "3" },
  { label: "thursday", value: "4" },
  { label: "friday", value: "5" },
  { label: "saturday", value: "6" },
  { label: "sunday", value: "7" },
];

const monthlyTimeOptions = [
  {
    value: "0",
    label: (
      <Translate id="design_approval_process.progress-settings-modal.monthly-time-options.start-of-month" />
    ),
  },
  {
    value: "1",
    label: (
      <Translate id="design_approval_process.progress-settings-modal.monthly-time-options.end-of-month" />
    ),
  },
];

const Options = ({
  workflowType,
  autoFormTemplateId,
  autoRepeatCycle,
  autoRepeatCycleValues,
  autoRepeatTime,
  translate,
  handleAction,
  site_set_path,
  site_set_path_ids,
  ...props
}) => {
  const [monthlyValue, setMonthlyValue] = useState("0");
  const [weeklyValue, setWeeklyValue] = useState(["1"]);
  const [allSiteSets, setAllSiteSets] = useState([]);
  const [siteSetId, setSiteSetId] = useState(null);
  const [siteSetDetailId, setSiteSetDetailId] = useState(null);
  const [formTemplatesList, setFormTemplatesList] = useState([]);
  const [locationSet, setLocationSet] = useState([]);
  const [locationSetRoot, setLocationSetRoot] = useState(null);
  const [selectedLocationSet, setSelectedLocationSet] = useState([]);
  const [viewNext, setViewNext] = useState(false);
  const [isLoadingSiteSetDetails, setIsLoadingSiteSetDetails] = useState(true);

  const { project } = useProject();
  const location = useLocation();

  function handleWeeklyChange(day) {
    if (weeklyValue.includes(day))
      setWeeklyValue(weeklyValue.filter((item) => item !== day));
    else setWeeklyValue([...weeklyValue, day]);
  }

  const getFormTemplates = () => {
    const payload = {
      attributes: [],
      site_set_id: siteSetId,
      site_set_level: selectedLocationSet?.length,
    };

    const url = `/form_templates/filters?project_id=${project.id}&size=1000`;
    API.postData(url, payload).then(({ data: res }) => {
      let formTemplates = [];
      res.data.forEach((formTemplate) => {
        formTemplates.push({
          id: formTemplate.id,
          value: formTemplate.id.toString(),
          label: formTemplate.name,
        });
      });
      setFormTemplatesList(formTemplates);
    });
  };

  const setSiteSetDetailByID = (id) => {
    let siteSet = locationSet.find((item) => item.id === id);
    setSelectedLocationSet([...selectedLocationSet, siteSet]);

    if (!siteSet.is_details) {
      setViewNext(false);
    }
    getSiteSetDetailByID(id);
  };

  const selectedLocationSetHandler = (index) => {
    let parentId = null;
    let newSelectedLocationSet = [];
    if (index > 0) {
      parentId = selectedLocationSet[index - 1].id;
      newSelectedLocationSet = selectedLocationSet.slice(0, index);

      getSiteSetDetailByID(parentId);
    } else if (index === 0) {
      parentId = locationSetRoot.value;
      newSelectedLocationSet = [];
      getSiteSetByID(parentId);
    }
    setViewNext(true);
    setSelectedLocationSet(newSelectedLocationSet);
    setSiteSetDetailId(null);
  };

  const getSiteSetByID = (id) => {
    setIsLoadingSiteSetDetails(true);

    API.getData(`site_set/${id}`).then(({ data: res }) => {
      setLocationSet(res.data.details);
      setSiteSetId(res.data.id);

      setIsLoadingSiteSetDetails(false);
    });
  };

  const getSiteSetDetailByID = (id) => {
    setIsLoadingSiteSetDetails(true);

    API.getData(`site_set_detail/${id}`).then(({ data: res }) => {
      setLocationSet(res.data.details);
      setSiteSetDetailId(res.data.id);
      setIsLoadingSiteSetDetails(false);
      setViewNext(Boolean(res.data.details.length));
    });
  };

  const getAllSiteSetsByProjectID = () => {
    API.getData(`site_sets?project_id=${project.id}`).then(({ data: res }) => {
      setAllSiteSets(res.data);
    });
  };

  const allSiteSetsHandler = (siteSet) => {
    setLocationSetRoot(siteSet);
    setSiteSetDetailId(null);
    getSiteSetByID(siteSet.value);
    setViewNext(true);
  };

  useEffect(() => {
    getAllSiteSetsByProjectID();
    if (autoRepeatCycle === "monthly") {
      setMonthlyValue(autoRepeatCycleValues);
    } else if (autoRepeatCycle === "weekly") {
      setWeeklyValue(autoRepeatCycleValues?.split(","));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleAction({
      site_set_detail_id: siteSetDetailId,
    });
    if (siteSetDetailId) {
      getFormTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteSetDetailId]);

  useEffect(() => {
    if (!site_set_path_ids || !site_set_path) return;

    const siteSetPaths = site_set_path_ids
      ?.split(">")
      ?.map((item) => item.trim());

    const siteSetPathNames = site_set_path
      ?.split(">")
      ?.map((item) => item.trim());

    const siteSetPathIds = siteSetPaths?.map((item, index) => {
      if (index === 0)
        return { value: parseInt(item), label: siteSetPathNames[index] };
      else
        return {
          id: parseInt(item),
          name: siteSetPathNames[index],
        };
    });

    setLocationSetRoot(siteSetPathIds[0]);
    setSiteSetId(siteSetPathIds[0].value);

    const pathItems = siteSetPathIds?.slice(1, siteSetPathIds.length);
    setSelectedLocationSet(pathItems);

    const lastItem = siteSetPathIds[siteSetPathIds.length - 1];
    setSiteSetDetailId(lastItem.id);

    handleAction({
      site_set_detail_id: lastItem.id,
    });

    if (!location.pathname.includes("new")) getSiteSetDetailByID(lastItem.id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site_set_path_ids]);

  useEffect(() => {
    handleAction({
      auto_repeat_cycle_values:
        autoRepeatCycle === "weekly"
          ? weeklyValue?.join(",")
          : autoRepeatCycle === "monthly"
          ? monthlyValue
          : autoRepeatCycle === "daily"
          ? null
          : autoRepeatCycleValues,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRepeatCycle, weeklyValue, monthlyValue]);

  return (
    <div className="row mx-2">
      <div className="col-12 border-bottom border-3 pb-4">
        <div className="mt-1">
          <Radio
            index="0"
            label={translate(
              "design_approval_process.progress-settings-modal.manual"
            )}
            checked={workflowType === "manual"}
            onChecked={() => handleAction({ workflow_type: "manual" })}
          />
        </div>
        <div className="mt-3">
          <Radio
            index="1"
            label={translate(
              "design_approval_process.progress-settings-modal.auto"
            )}
            checked={workflowType === "auto"}
            onChecked={() =>
              handleAction({
                workflow_type: "auto",
                auto_repeat_cycle: "daily",
                auto_repeat_cycle_values: null,
                auto_repeat_time: "00:00",
              })
            }
          />
        </div>
      </div>
      {workflowType === "auto" && (
        <div className="row">
          <p className="text-gray text-uppercase fw-bold mb-0 mt-4">
            Lokasyon Seçimi
          </p>
          <div className="col-12 col-md-4 ps-0 my-2 mx-2">
            <div className="w-100 d-flex align-items-center justify-content-center px-2 py-2 me-0 me-md-4 bg-white shadow-sm rounded border-gray border">
              <SelectBox
                options={allSiteSets?.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                onChange={(e) => {
                  allSiteSetsHandler(e);
                  setSelectedLocationSet([]);
                }}
                value={locationSetRoot}
                placeholder={translate("forms.place-holders.select-location")}
                height="42px"
                width="100%"
              />
            </div>
          </div>
          {locationSetRoot && (
            <div className="col-12 py-2 bg-white shadow-sm rounded border-gray border mx-2 mb-3">
              <div className="d-flex flex-wrap align-items-center">
                {locationSetRoot ? (
                  <div
                    className="d-flex align-items-center py-1"
                    onClick={() => selectedLocationSetHandler(0)}
                  >
                    <MdLocationOn className="fs-2 text-orange" />
                    <p className="mb-0 ps-1 pe-2">{locationSetRoot.label}</p>
                  </div>
                ) : null}

                {selectedLocationSet?.map((location, index) => (
                  <div
                    className="d-flex flex-wrap align-items-center"
                    key={index}
                    onClick={() => selectedLocationSetHandler(index)}
                  >
                    <MdOutlineArrowForwardIos />
                    <p className="mb-0 px-2">{location.name}</p>
                  </div>
                ))}

                {viewNext && (
                  <div className="d-flex flex-wrap align-items-center">
                    <MdOutlineArrowForwardIos className="me-2" />
                    <SelectBox
                      width="250px"
                      options={locationSet?.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      isMulti={false}
                      value={[]}
                      onChange={(e) => setSiteSetDetailByID(e.value)}
                      placeholder={
                        isLoadingSiteSetDetails
                          ? translate("loading")
                          : translate("please_select")
                      }
                      isDisabled={isLoadingSiteSetDetails}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="col-12 mt-3 mb-2">
            <p className="text-gray text-uppercase fw-bold mb-2">
              {translate(
                "design_approval_process.progress-settings-modal.form-template"
              )}
            </p>
            <SelectBox
              options={formTemplatesList}
              disabled={siteSetDetailId ? false : true}
              onChange={(data) =>
                handleAction({ auto_form_template_id: data.value })
              }
              placeholder={translate(
                "design_approval_process.progress-settings-modal.placeholders.select-form-template"
              )}
              value={
                autoFormTemplateId
                  ? formTemplatesList.find(
                      (item) => item.value === autoFormTemplateId.toString()
                    )
                  : null
              }
            />
          </div>
          <div className="col-12 d-flex border-top border-3 my-4 pt-3">
            <div className="col-3">
              <div className="mt-3">
                <Radio
                  index="3"
                  label={translate(
                    "design_approval_process.progress-settings-modal.daily"
                  )}
                  checked={autoRepeatCycle === "daily"}
                  onChecked={() => handleAction({ auto_repeat_cycle: "daily" })}
                />
              </div>
              <div className="mt-3">
                <Radio
                  index="4"
                  label={translate(
                    "design_approval_process.progress-settings-modal.weekly"
                  )}
                  checked={autoRepeatCycle === "weekly"}
                  onChecked={() =>
                    handleAction({ auto_repeat_cycle: "weekly" })
                  }
                />
              </div>
              <div className="mt-3">
                <Radio
                  index="5"
                  label={translate(
                    "design_approval_process.progress-settings-modal.monthly"
                  )}
                  checked={autoRepeatCycle === "monthly"}
                  onChecked={() =>
                    handleAction({ auto_repeat_cycle: "monthly" })
                  }
                />
              </div>
            </div>
            <div className="col-5 d-flex justify-content-start border-left border-left-light-gray ps-5 border-2">
              {autoRepeatCycle === "weekly" ? (
                <div>
                  {weekDays.map((day, index) => (
                    <div className="mt-3" key={index}>
                      <Checkbox
                        name={day.value}
                        checked={weeklyValue?.includes(day.value) || false}
                        onChange={() => handleWeeklyChange(day.value)}
                        label={translate(
                          `design_approval_process.progress-settings-modal.week-days.${day.label}`
                        )}
                      />
                    </div>
                  ))}
                </div>
              ) : autoRepeatCycle === "monthly" ? (
                <div className="mt-3 mb-2 me-3">
                  <p className="text-gray text-uppercase fw-bold mb-2">
                    {translate(
                      "design_approval_process.progress-settings-modal.time"
                    )}
                  </p>
                  <SelectBox
                    options={monthlyTimeOptions}
                    onChange={(data) => setMonthlyValue(data.value)}
                    placeholder={translate(
                      "design_approval_process.progress-settings-modal.placeholders.select-time"
                    )}
                    value={
                      monthlyValue
                        ? monthlyTimeOptions[parseInt(monthlyValue)]
                        : null
                    }
                  />
                </div>
              ) : (
                ``
              )}
            </div>
            <div className="col-3 mt-3">
              <p className="text-gray text-uppercase fw-bold mb-2">
                {translate(
                  "design_approval_process.progress-settings-modal.hour"
                )}
              </p>
              <Input
                type="time"
                defaultValue={autoRepeatTime}
                onChange={(e) =>
                  handleAction({ auto_repeat_time: e.target.value })
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Options.defaultProps = {
  workflowType: "manual",
  autoFormTemplateId: null,
  autoRepeatCycle: "daily",
  autoRepeatCycleValues: "",
  autoRepeatTime: "00:00",
};

export default Options;
